/**
 * GRN on-time performance chart for a PO line.
 * Shows percentage breakdown: On Time / Early / Late across all GRNs for the same PO line.
 * CR-4614: Click-through drill-down showing full GRN list.
 * CR-4648: Drill-down table now exposes per-row Appeal Status + Action so
 * users can acknowledge / appeal / cancel-appeal / respond directly from
 * the sibling-deliveries view, mirroring the buttons already offered on
 * the parent Delivery Details modal but scoped to the clicked GRN.
 * CR-7105: Drill-down now shows the ODD (bold in the header at PO-line
 * grain; per-row column at schedule grain, where each delivery is judged
 * against its own schedule date — same derivation as DeliveryDetailsModal /
 * CR-4607) and offers a read-only View Appeal on rows carrying an appeal.
 * Row cells live in GRNScoreChartRowActions.tsx (500-line cap).
 */
import { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { purchasingAPI } from '../../../lib/api';
import type { GoodsReceiptNote, DeliveryAppeal, DeliveryAppealStatus } from '../../../lib/api/types/grn-types';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { ON_TIME_STATUS_LABELS } from '../../../lib/onTimeStatus';
import { AcknowledgeGRNModal, type GRNWorkflowData } from '../GRNWorkflowModals';
import RaiseAppealModal from './RaiseAppealModal';
import AppealResponseModal from './AppealResponseModal';
import ViewAppealModal from './ViewAppealModal';
import { RowActions, appealStatusCell } from './GRNScoreChartRowActions';
import { ORIGINAL_REQUIRED_DATE } from '../../../config/dateLabels';

interface GRNScoreChartProps {
  poLineId: number;
  poNumber?: string;
  lineNumber?: number;
  /** CR-7106: fired after any successful mutation inside the drill-down
   * (acknowledge / raise / update / cancel / respond appeal). Every other
   * mutation path already refetches the deliveries list; this drill-down was
   * the one path that refreshed only its own rows, leaving the outer list
   * and the parent Delivery Details modal stale. */
  onMutated?: () => void;
}

const COLORS: Record<string, string> = {
  on_time: '#22c55e',
  early: '#3b82f6',
  late: '#ef4444',
  // CR-7418: teal for the two overridden verdicts — a pass, so it belongs in
  // the green family, but distinct from a delivery that met its date on its
  // own merits. They used to fall through to grey "unknown".
  overridden_early: '#14b8a6',
  overridden_late: '#14b8a6',
  unknown: '#9ca3af',
};

//: CR-7418: the words come from the vocabulary file that owns the statuses;
//: this map only adds the one value that is not a status at all.
const LABELS: Record<string, string> = {
  ...ON_TIME_STATUS_LABELS,
  unknown: 'Unknown',
};

export default function GRNScoreChart({ poLineId, poNumber, lineNumber, onMutated }: GRNScoreChartProps) {
  const [data, setData] = useState<{ name: string; value: number; color: string }[]>([]);
  const [total, setTotal] = useState(0);
  const [grns, setGrns] = useState<GoodsReceiptNote[]>([]);
  const [showDrilldown, setShowDrilldown] = useState(false);
  const [actionGrn, setActionGrn] = useState<GoodsReceiptNote | null>(null);
  const [acknowledgeOpen, setAcknowledgeOpen] = useState(false);
  const [appealOpen, setAppealOpen] = useState(false);
  const [respondAppeal, setRespondAppeal] = useState<DeliveryAppeal | null>(null);
  const [viewAppeal, setViewAppeal] = useState<DeliveryAppeal | null>(null);
  const [busy, setBusy] = useState(false);
  const { formatDate } = useFormatters();
  const { user } = useAuth();
  const { isFeatureEnabled, isOpenOrdersControlHidden } = useMenuSettings();
  // Schedule-grain tenants attribute deliveries to a schedule line, not the PO
  // line, so the performance breakdown is per schedule line.
  const scheduleGrain = isFeatureEnabled('po_line_schedules');
  const grainLabel = scheduleGrain ? 'Schedule Line' : 'PO Line';
  const companyType = user?.company_type;
  // CR-5884 / CR-4648 rework: the same Admin Setup toggle that hides the
  // supplier's Appeal / Update-Appeal / Cancel-Appeal controls on the parent
  // Delivery Details modal must hide them in this drill-down too, or a tenant
  // that switched them off still sees them one click deeper.
  const hideAppealForSupplier = isOpenOrdersControlHidden('hide_otif_override_button', user?.platform === 'SUPPLIER');

  const fetchData = async () => {
    try {
      const res = await purchasingAPI.getGRNs({ po_line: poLineId, page_size: 100 });
      const grnList: GoodsReceiptNote[] = res.data.results || [];
      setGrns(grnList);
      const counts: Record<string, number> = { on_time: 0, early: 0, late: 0, unknown: 0 };
      for (const grn of grnList) {
        const status = grn.on_time_status || 'unknown';
        counts[status] = (counts[status] || 0) + 1;
      }
      setTotal(grnList.length);
      setData(
        Object.entries(counts)
          .filter(([, v]) => v > 0)
          .map(([key, value]) => ({
            name: LABELS[key] || key,
            value,
            color: COLORS[key] || COLORS['unknown'],
          }))
      );
    } catch {
      // ignore — empty list shows "no deliveries"
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poLineId]);

  if (total === 0) return null;

  const getStatusBadge = (status: string) => {
    const color = COLORS[status] || COLORS['unknown'];
    const label = LABELS[status] || 'Unknown';
    return (
      <span
        className="px-2 py-1 rounded-full text-xs font-medium text-white"
        style={{ backgroundColor: color }}
      >
        {label}
      </span>
    );
  };

  const handleAcknowledge = (grn: GoodsReceiptNote) => {
    setActionGrn(grn);
    setAcknowledgeOpen(true);
  };
  const handleAppeal = (grn: GoodsReceiptNote) => {
    setActionGrn(grn);
    setAppealOpen(true);
  };
  const handleRespondAppeal = async (grn: GoodsReceiptNote) => {
    if (!grn.latest_appeal?.id) return;
    try {
      setBusy(true);
      const res = await purchasingAPI.getDeliveryAppeal(grn.latest_appeal.id);
      setActionGrn(grn);
      setRespondAppeal(res.data as DeliveryAppeal);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load appeal'));
    } finally {
      setBusy(false);
    }
  };
  // CR-7105: read-only view of a row's appeal — same fetch as the respond
  // path, but no action context.
  const handleViewAppeal = async (grn: GoodsReceiptNote) => {
    if (!grn.latest_appeal?.id) return;
    try {
      setBusy(true);
      const res = await purchasingAPI.getDeliveryAppeal(grn.latest_appeal.id);
      setViewAppeal(res.data as DeliveryAppeal);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load appeal'));
    } finally {
      setBusy(false);
    }
  };
  const handleCancelAppeal = async (grn: GoodsReceiptNote) => {
    if (!grn.latest_appeal?.id) return;
    if (!window.confirm('Are you sure you want to cancel this appeal?')) return;
    try {
      setBusy(true);
      await purchasingAPI.cancelDeliveryAppeal(grn.latest_appeal.id);
      toast.success('Appeal cancelled');
      await fetchData();
      onMutated?.();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to cancel appeal'));
    } finally {
      setBusy(false);
    }
  };

  const handleAcknowledgeSubmit = async (data: GRNWorkflowData) => {
    try {
      await purchasingAPI.acknowledgeGRN(data.grnId, { comments: data.comments });
      toast.success('Delivery acknowledged');
      setAcknowledgeOpen(false);
      setActionGrn(null);
      await fetchData();
      onMutated?.();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to acknowledge delivery'));
      throw err;
    }
  };

  const refreshAfterAppealAction = async () => {
    setAppealOpen(false);
    setActionGrn(null);
    await fetchData();
    onMutated?.();
  };

  const handleRespondSuccess = async () => {
    setRespondAppeal(null);
    setActionGrn(null);
    await fetchData();
    onMutated?.();
  };

  const anyActionModalOpen = (acknowledgeOpen && !!actionGrn) || (appealOpen && !!actionGrn) || !!respondAppeal || !!viewAppeal;

  // CR-7105: at PO-line grain the ODD is a line property, so it belongs
  // once, bold, in the drill-down header. It is the verdict anchor (the
  // line's ODD — every row carries the same one at this grain), not the
  // stored delivery_date, which a feed can re-plan away from it. At schedule
  // grain each delivery is judged against its own schedule date, so a single
  // header date would be wrong (the CR-4607 trap) — a per-row column renders
  // instead.
  const lineOdd = grns[0]?.verdict_anchor_date ?? grns[0]?.original_delivery_date
    ?? grns[0]?.po_delivery_date ?? null;

  return (
    <>
      <div className="mt-4 pt-4 border-t">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Delivery Performance ({grainLabel})</h4>
        <div
          className="flex items-center gap-4 cursor-pointer hover:bg-gray-50 rounded p-2 -m-2 transition-colors"
          onClick={() => setShowDrilldown(true)}
          title="Click to view details"
        >
          <ResponsiveContainer width={120} height={120}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={50}
                dataKey="value"
                strokeWidth={1}
              >
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => [`${value} / ${total}`, '']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 text-xs">
            {data.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
                <span>{entry.name}: {entry.value} ({Math.round((entry.value / total) * 100)}%)</span>
              </div>
            ))}
            <div className="text-gray-400 mt-1">Total: {total.toLocaleString()} {total === 1 ? 'delivery' : 'deliveries'} (click for details)</div>
          </div>
        </div>
      </div>

      {/* CR-4614: Drill-down modal. CR-4648: + Appeal Status / Action columns. */}
      {showDrilldown && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[80]" onClick={() => setShowDrilldown(false)} />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="text-lg font-bold">
                  Delivery Performance
                  {poNumber && <span className="text-sm font-normal text-gray-500 ml-2">PO: {poNumber}{lineNumber ? ` / Line ${lineNumber}` : ''}</span>}
                  {/* CR-7105: the date appeals are judged against, bold, at
                      PO-line grain only — at schedule grain it varies per row
                      and renders as a column instead. */}
                  {!scheduleGrain && lineOdd && (
                    <span className="text-sm font-normal text-gray-500 ml-3">{ORIGINAL_REQUIRED_DATE}: <span className="font-bold text-gray-900">{formatDate(lineOdd)}</span></span>
                  )}
                </h3>
                <button onClick={() => setShowDrilldown(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-5 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left py-2 px-3 font-medium">Delivery Date</th>
                      {scheduleGrain && <th className="text-left py-2 px-3 font-medium">{ORIGINAL_REQUIRED_DATE}</th>}
                      <th className="text-left py-2 px-3 font-medium">GRN Number</th>
                      <th className="text-right py-2 px-3 font-medium">Qty Delivered</th>
                      <th className="text-left py-2 px-3 font-medium">On-Time Status</th>
                      <th className="text-left py-2 px-3 font-medium">Appeal Status</th>
                      <th className="text-left py-2 px-3 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grns.map((grn) => (
                      <tr key={grn.id} className="border-b hover:bg-gray-50 align-top">
                        <td className="py-2 px-3">{formatDate(grn.receipt_date)}</td>
                        {/* CR-7105: per-row judged-against date at schedule
                            grain — same derivation as DeliveryDetailsModal
                            (CR-4607) so the two surfaces never disagree. */}
                        {scheduleGrain && (() => {
                          const rowOdd = grn.schedule_date ?? grn.original_delivery_date ?? grn.po_delivery_date;
                          return <td className="py-2 px-3">{rowOdd ? formatDate(rowOdd) : '-'}</td>;
                        })()}
                        <td className="py-2 px-3 font-mono">{grn.grn_number}</td>
                        <td className="py-2 px-3 text-right">{grn.quantity_received}</td>
                        <td className="py-2 px-3">{getStatusBadge(grn.on_time_status || 'unknown')}</td>
                        <td className="py-2 px-3">{appealStatusCell(grn)}</td>
                        <td className="py-2 px-3">
                          <RowActions
                            grn={grn}
                            companyType={companyType}
                            busy={busy}
                            hideAppealForSupplier={hideAppealForSupplier}
                            onAcknowledge={handleAcknowledge}
                            onAppeal={handleAppeal}
                            onCancelAppeal={handleCancelAppeal}
                            onRespondAppeal={handleRespondAppeal}
                            onViewAppeal={handleViewAppeal}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-gray-500 mt-3">Showing {grns.length} of {total.toLocaleString()} {total === 1 ? 'delivery' : 'deliveries'}</p>
              </div>
              <div className="flex justify-end p-5 border-t">
                <button
                  onClick={() => setShowDrilldown(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* CR-4648: per-row action modals. Reuse the same components the
          parent Delivery Details footer launches, but scoped to the
          row's GRN so the user doesn't have to close + reopen the
          drill-down to act on a sibling delivery.

          CR-4648 rework: these modals hardcode their own z-index
          (Acknowledge z-50, Raise/Respond Appeal z-[60]/z-[70]), all of
          which sit *below* the drill-down's z-[80]/z-[90] — so opening
          one from a drill-down row left it stranded behind the
          drill-down and unusable. Wrapping them in a positioned z-[100]
          element gives the whole group its own stacking context above
          the drill-down, without changing the shared modals (which are
          also launched from the parent Delivery Details footer, where
          their existing z-index is correct). */}
      {anyActionModalOpen && (
        <div className="relative z-[100]">
          {acknowledgeOpen && actionGrn && (
            <AcknowledgeGRNModal
              isOpen={acknowledgeOpen}
              onClose={() => { setAcknowledgeOpen(false); setActionGrn(null); }}
              grnId={actionGrn.id}
              grnNumber={actionGrn.grn_number}
              poNumber={actionGrn.po_number || ''}
              partNumber={actionGrn.part_number || ''}
              onSubmit={handleAcknowledgeSubmit}
            />
          )}
          {appealOpen && actionGrn && (
            <RaiseAppealModal
              isOpen={appealOpen}
              onClose={() => { setAppealOpen(false); setActionGrn(null); }}
              grn={actionGrn}
              existingAppealId={
                actionGrn.latest_appeal && (actionGrn.latest_appeal.status as DeliveryAppealStatus) === 'queried'
                  ? actionGrn.latest_appeal.id
                  : undefined
              }
              onSuccess={refreshAfterAppealAction}
            />
          )}
          {respondAppeal && (
            <AppealResponseModal
              isOpen={!!respondAppeal}
              onClose={() => { setRespondAppeal(null); setActionGrn(null); }}
              appeal={respondAppeal}
              onSuccess={handleRespondSuccess}
            />
          )}
          {viewAppeal && (
            <ViewAppealModal
              isOpen={!!viewAppeal}
              onClose={() => setViewAppeal(null)}
              appeal={viewAppeal}
            />
          )}
        </div>
      )}
    </>
  );
}
